import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ShieldCheck, LogOut } from 'lucide-react';

/**
 * TermsModal — disclaimer legal Tectur 4x4 (TT navigation).
 * Aparece logo após o login se o user ainda não aceitou.
 * Persistência: conta (users.terms_accepted_at) + localStorage.
 * Sem botão "Recusar". Para sair → confirmação → logout.
 */
export default function TermsModal({ open, onAccept, onLogout }) {
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  return (
    <Dialog open={open} onOpenChange={() => { /* não fecha sem aceitar/sair */ }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="terms-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            Importante: Leia atentamente antes de iniciar a navegação
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 text-sm leading-relaxed text-foreground/85 space-y-4">
          <p>
            Ao utilizar esta aplicação de navegação Todo-o-Terreno (TT), o utilizador aceita,
            de forma expressa, consciente e voluntária, todos os termos, condições e riscos
            descritos em seguida:
          </p>

          <section>
            <h3 className="font-semibold text-foreground mb-1">1. Natureza Informativa dos Dados</h3>
            <p>
              Os mapas, rotas, tracks (GPX) e pontos de interesse disponibilizados por esta
              aplicação servem <strong>exclusivamente como guia informativo e de lazer</strong>.
              O meio natural e as pistas de TT são dinâmicos e estão sujeitos a alterações
              constantes devido a condições meteorológicas, erosão, intervenção humana ou
              força maior.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-foreground mb-1">2. Inexistência de Responsabilidade por Danos Materiais e Pessoais</h3>
            <p className="mb-2">
              A entidade proprietária e os programadores desta aplicação (<strong>Tectur</strong>)
              não serão, em caso algum, responsabilizados por quaisquer danos, diretos ou
              indiretos, causados:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Nas viaturas:</strong> incluindo avarias mecânicas, danos na
                carroçaria, furos, capotamentos ou perda total do veículo.
              </li>
              <li>
                <strong>Em propriedade privada ou pública:</strong> danos em terrenos,
                culturas, vedações, portões ou infraestruturas.
              </li>
              <li>
                <strong>A pessoas:</strong> lesões corporais, ferimentos ou morte do
                utilizador, passageiros ou terceiros.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-foreground mb-1">3. Responsabilidade Exclusiva do Utilizador</h3>
            <p className="mb-2">O utilizador é o único e inteiro responsável por:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Avaliar as condições reais:</strong> confrontar as indicações da
                aplicação com a realidade do terreno e decidir, de forma autónoma, se é
                seguro avançar. <em>A realidade do terreno sobrepõe-se sempre às instruções
                da aplicação.</em>
              </li>
              <li>
                <strong>Cumprimento legal:</strong> garantir que circula em vias públicas,
                caminhos autorizados ou propriedade privada com a devida permissão,
                respeitando escrupulosamente a legislação ambiental e o Código da Estrada.
              </li>
              <li>
                <strong>Capacidade técnica:</strong> garantir que possui a experiência de
                condução necessária e que o veículo se encontra devidamente preparado,
                legalizado e equipado para o percurso pretendido.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-foreground mb-1">4. Isenção de Garantia de Precisão</h3>
            <p>
              Não garantimos a precisão milimétrica, exatidão ou atualização em tempo real
              dos mapas e rotas. O utilizador assume todos os riscos associados à perda de
              sinal de GPS, erros de cartografia ou caminhos temporariamente obstruídos,
              intransitáveis ou proibidos.
            </p>
          </section>

          <p className="text-muted-foreground italic">
            Ao clicar em <strong>&ldquo;Aceito os Termos e Condições&rdquo;</strong> ou ao continuar a
            utilizar esta aplicação, o utilizador declara que leu, compreendeu e aceita
            vincular-se a este Termo de Responsabilidade, isentando a Tectur de qualquer
            reclamação judicial ou extrajudicial.
          </p>
        </div>

        <div className="border-t bg-muted px-6 py-4 shrink-0 flex flex-col sm:flex-row gap-2 justify-end">
          {confirmingLogout ? (
            <>
              <div className="flex-1 text-xs text-amber-500 self-center">
                Tens a certeza? Ao sair fazes logout e voltas ao ecrã de login.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmingLogout(false)}
                data-testid="terms-cancel-logout-btn"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={onLogout}
                data-testid="terms-confirm-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1" /> Sair (logout)
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmingLogout(true)}
                data-testid="terms-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-1" /> Sair
              </Button>
              <Button
                onClick={onAccept}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="terms-accept-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-1" /> Aceito os Termos e Condições
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
